import Tabs from "../tabs";
import { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
  Tooltip,
  CardMedia,
  Typography,
  Button
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import MultiSelectWithAll from "../../../../components/selections/multiSelectWithAll";
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import DataTableWithSearch from "../../../../components/dataTableWithSearch/DataTableWithSearch";
import SingleSelect from "../../../../components/selections/singleSelect";
import PreviewIcon from '@mui/icons-material/Preview';
import ViewFileModal from "./viewFileModal";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getIndividualUserThunk } from "../../../contact/contactThunk";
import ContactProfileTabModal from "../../../contactProfile/contactProfileTabModal";

export default function FilesList() {
  const dispatch = useDispatch();

  const { getFileUsers, status, error, getFileClients } = useSelector((state) => state.fileCategories);
  const [openModal, setOpenModal] = useState(false);
  const [rowData, setRowData] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [state, setState] = useState({
    selectedValue: "1",
    selectedBrachDeptValue: [],
    branchDepartmentId: "",
    selectCategory: "",
    categoryOptions: [],
    openContactEditModal: false,
    selectedRowData: null,
  });

  const { selectedValue, selectedBrachDeptValue, branchDepartmentId, selectCategory, selectedRowData } = state;

  // Allocate user-level details
  const allocateUserLevelDetails = useMemo(() => {
    if (!getFileUsers || getFileUsers.length === 0) return [];

    return getFileUsers.reduce((acc, current) => {
      const existing = acc.find(item => item.wa_contact_id === current.wa_contact_id);

      if (existing) {
        existing.file_url.push(current.file_url);
        existing.file_name.push(current.file_name);
      } else {
        acc.push({
          wa_contact_id: current.wa_contact_id,
          name: current.name || "-",
          mobile: current.mobile || "-",
          category_id: current.category_id || "",
          category_name: current.category_name || "-",
          branch_department_id: current.branch_department_id || "",
          department_name: current.department_name || "-",
          department_id: current.department_id || "",
          branch_name: current.branch_name || "-",
          branch_id: current.branch_id || "",
          branch_department_name: current.branch_department_name || "-",
          file_url: [current.file_url],
          file_name: [current.file_name],
          file_type: current.file_type || "",
        });
      }
      return acc;
    }, []);
  }, [getFileUsers]);

  // Build dropdown options
  const buildOptions = (items, idKey, nameKey, placeholder) => {
    const map = new Map();
    items?.forEach(item => {
      const ids = (item[idKey] || "").split(",").map(x => x.trim());
      const names = (item[nameKey] || "").split(",").map(x => x.trim());

      ids.forEach((id, idx) => {
        if (!id) return;
        const label = names[idx] || id;
        if (!map.has(id)) map.set(id, { value: id, label });
      });
    });

    // Add "Un Assign" if any item has empty idKey
    const hasUnassigned = items?.some(item => !item[idKey]);
    const options = [...map.values()];
    if (hasUnassigned) options.unshift({ value: "0", label: placeholder });

    return options;
  };

  // Memoize branchDeptOptions to prevent infinite loop
  const branchDeptOptions = useMemo(() => {
    return buildOptions(
      selectedValue === "1" ? getFileUsers : getFileClients,
      "branch_department_id",
      "branch_department_name",
      "Un Assign"
    );
  }, [selectedValue, getFileUsers, getFileClients]);

  // Filter function for users
  const filterUsers = (contacts, key, selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) return [];

    const includeAll = selectedOptions.some(opt => opt.value === "0");
    const selectedIds = selectedOptions.filter(opt => opt.value !== "0").map(opt => opt.value);

    return contacts.filter(contact => {
      const contactValue = contact[key] || "";
      const contactIds = contactValue.split(",").map(x => x.trim()).filter(Boolean);

      if (includeAll && contactIds.length === 0) return true;
      if (includeAll) return true;

      return contactIds.some(id => selectedIds.includes(id));
    });
  };

  // Filtered users for table
  const filteredUsers = useMemo(() => {
    return filterUsers(allocateUserLevelDetails, "branch_department_id", selectedBrachDeptValue);
  }, [allocateUserLevelDetails, selectedBrachDeptValue]);

  // Table columns
  const userColumns = useMemo(() => [
    { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
    {
      accessorKey: "name",
      header: "Name",
      Cell: ({ row }) => (
        <span
          style={{ display: "inline-block" }}
        >
          <Box
            onClick={() => {
              // navigate(APP_ROUTES.PROFILE);
              setState(prev => ({ ...prev, openContactEditModal: true, selectedRowData: row?.original }));
              dispatch(getIndividualUserThunk({ wa_contact_id: row.original.wa_contact_id }));
            }}
            component="span"
            sx={{
              cursor: "pointer",
              color: "blue",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {row.original.name}
          </Box>
        </span>
      )
    },
    { accessorKey: "mobile", header: "Mobile" },
    { accessorKey: "department_name", header: "Department Name" },
    { accessorKey: "branch_name", header: "Branch Name" },
  ], [dispatch]);

  // Handle multi-select change
  const handleMultiSelectChange = (name, selectedValues) => {
    setState(prev => ({
      ...prev,
      [name]: selectedValues,
    }));
  };

  // Handle radio option change
  const handleOptionChange = (event) => {
    setState(prev => ({
      ...prev,
      selectedValue: event.target.value,
      branchDepartmentId: "",
      selectCategory: "",
      categoryOptions: [],
      selectedBrachDeptValue: [],
    }));
  };

  // Render filters
  const renderUserFilter = () => (
    <Grid container spacing={1}>
      <Grid item size={{ xs: 12, md: 3 }}>
        <MultiSelectWithAll
          name="selectedBrachDeptValue"
          label="Select Branch Dept"
          options={branchDeptOptions}
          value={selectedBrachDeptValue || []}
          onChange={handleMultiSelectChange}
        />
      </Grid>
    </Grid>
  );

  // Snackbar for errors
  useEffect(() => {
    if (status === "failed") {
      setSnackbar({
        open: true,
        message: error,
        severity: "error",
      });
    }
  }, [status, error]);

  // Set default selected options once
  useEffect(() => {
    if (branchDeptOptions?.length && selectedBrachDeptValue?.length === 0) {
      setState(prev => ({
        ...prev,
        selectedBrachDeptValue: branchDeptOptions,
      }));
    }
  }, [branchDeptOptions]);

  const handleSingleSelectChange = (name, value) => {
    setState(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedDepartment = getFileClients?.find(item => +item.branch_department_id === +branchDepartmentId);
  const selectedCategoryFiles = selectedDepartment?.category?.find(cat => +cat.category_id === +selectCategory);
  const categoryOptionArr = selectedDepartment?.category?.map(cat => ({
    value: cat.category_id,
    label: cat.category_name
  }));

  const clientLevelFields = [
    { id: 1, name: "branchDepartmentId", value: branchDepartmentId, type: "selection", options: branchDeptOptions, label: "Select Branch Dept" },
    { id: 2, name: "selectCategory", value: selectCategory, type: "selection", options: categoryOptionArr, label: "Category", disabled: !branchDepartmentId },
  ];

  return (
    <div>
      <Tabs />

      <Box mt={2}>
        <FormControl component="fieldset">
          <RadioGroup
            name="my-radio-group"
            value={selectedValue}
            onChange={handleOptionChange}
            row
          >
            <FormControlLabel value="1" control={<Radio />} label="user-level" />
            <FormControlLabel value="2" control={<Radio />} label="client-level" />
          </RadioGroup>
        </FormControl>

        <Card sx={{ maxHeight: selectedValue === "2" && "70vh", overflow: selectedValue === "2" && "auto" }}>
          <CardContent>

            {selectedValue === "1" && (
              <DataTableWithSearch
                status={status}
                columns={userColumns}
                data={filteredUsers || []}
                renderFilters={renderUserFilter()}
              />
            )}

            {selectedValue === "2" && (
              <Grid container spacing={1}>
                {clientLevelFields.map((field) => (
                  <Grid key={field.id} item size={{ xs: 12, md: 3 }}>
                    <SingleSelect
                      name={field.name}
                      label={field.label}
                      options={field.options || []}
                      value={field.value}
                      onChange={handleSingleSelectChange}
                      disabled={field.disabled}
                      formHelperText={false}
                    />
                  </Grid>
                ))}

                <Grid item size={{ xs: 12, md: 12 }}>
                  {selectedDepartment && (
                    <Grid container spacing={1}>
                      {[selectedCategoryFiles].map(dept => (
                        (dept?.files || []).map((file, index) => (
                          <Grid item size={{ xs: 12, md: 3 }} key={index} sx={{ mt: 2 }}>
                            <Card key={file.id}>
                              {/* Preview */}

                              {file.file_type.startsWith("image/") ? (
                                <CardMedia component="img" image={file.file_url} alt={file.file_name} sx={{ height: 140, objectFit: "cover" }} />
                              ) : file.file_type === "video/mp4" ? (
                                <CardMedia component="video" src={file.file_url} controls sx={{ height: 140, objectFit: "cover" }} />
                              ) : file.file_type === "application/pdf" ? (
                                <Box sx={{ height: 140, display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#f5f5f5" }}>
                                  <PictureAsPdfIcon sx={{ fontSize: 50, color: "red" }} />
                                </Box>
                              ) : (
                                <Box sx={{ height: 140, display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#f5f5f5" }}>
                                  <Typography>File</Typography>
                                </Box>
                              )}

                              <CardContent>
                                <Grid container spacing={1}>
                                  <Grid item size={{ xs: 12, md: 10 }} mt={1}>
                                    <Tooltip title={file.file_name} arrow>
                                      <Typography
                                        variant="body2"
                                        noWrap
                                        component="span"
                                        sx={{
                                          cursor: "pointer",
                                          color: "blue",
                                          textDecoration: "none",
                                          "&:hover": {
                                            textDecoration: "underline",
                                          },
                                        }}
                                      >
                                        {file.file_name || "-"}
                                      </Typography>
                                    </Tooltip>
                                  </Grid>

                                  <Grid size={{ xs: 12, md: 2 }}>
                                    <Button onClick={() => { setOpenModal(true); setRowData(file) }}><PreviewIcon sx={{ color: "var(--btn-7)" }} /></Button>
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))
                      ))}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Box>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />

      <ViewFileModal show={openModal} handleClose={() => { setOpenModal(false); setRowData([]) }} type="fileDisplay" rowData={rowData} />

      <ContactProfileTabModal
        show={state.openContactEditModal}
        handleClose={() => setState(prev => ({ ...prev, openContactEditModal: false }))}
        rowData={selectedRowData}
        type="fileContacts"
      />
    </div>
  );
}
